import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import memberService from '../../services/memberService';
import { useTenant } from '../../contexts/TenantContext';

// Import Material UI components
import {
  Box, Grid, Typography, Card, CardContent, Avatar,
  Paper, Button, CircularProgress, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Chip, 
  Alert, Container
} from '@mui/material';

// Import Material UI icons
import {
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  List as ListIcon,
  ArrowForward as ArrowForwardIcon,
  Dashboard as DashboardIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

// Add the StatCard component 
const StatCard = ({ title, value, icon, color }) => (
  <Card elevation={2} sx={{ 
    height: '100%', 
    borderLeft: `4px solid ${color}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 3
    }
  }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box display="flex" alignItems="center">
        <Avatar sx={{ 
          bgcolor: `${color}20`, 
          color: color,
          width: { xs: 40, sm: 45 },
          height: { xs: 40, sm: 45 }
        }}>
          {icon}
        </Avatar>
        <Box ml={2} sx={{ width: 'calc(100% - 60px)' }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'medium',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const StaffDashboardPage = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  
  // Dashboard metrics state
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    pendingMembers: 0,
    expiredMembers: 0
  });
  const [recentMembers, setRecentMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setError(null);
        setLoading(true);
        console.log('Fetching dashboard data...');
        
        const response = await memberService.getDashboardStats();
        console.log('Dashboard data received:', response);
        
        // Extract the actual data from the response and log each step
        let statsData = {};
        if (response?.success && response?.data) {
          statsData = response.data;
        } else if (response?.stats) {
          statsData = response.stats;
        } else if (response?.data?.stats) {
          statsData = response.data.stats;
        } else if (response && typeof response === 'object') {
          statsData = response;
        }
        
        // Ensure all numeric values are properly converted from strings if needed
        const newStats = {
          totalMembers: Number(statsData.totalMembers || 0),
          activeMembers: Number(statsData.activeMembers || 0),
          pendingMembers: Number(statsData.pendingMembers || 0),
          expiredMembers: Number(statsData.expiredMembers || 0)
        };
        
        setStats(newStats);
        
        // Handle different member response structures
        let membersData = [];
        if (response?.recentMembers) {
          membersData = response.recentMembers;
        } else if (response?.data?.recentMembers) {
          membersData = response.data.recentMembers;
        } else if (response?.members) {
          membersData = response.members;
        }
        
        setRecentMembers(Array.isArray(membersData) ? membersData : []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !recentMembers.length && !stats.totalMembers) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">Error</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            onClick={() => window.location.reload()} 
            color="error"
            sx={{ mt: 1 }}
            size="small"
          >
            Refresh page
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}>
      {/* Header Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2, 
          background: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)', // Slight color variation for staff
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <DashboardIcon sx={{ mr: 1 }} /> Staff Dashboard
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8 }}>
              Overview of {tenant?.name || 'organization'}'s membership data
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} /> Membership Overview
          </Typography>
          <Button
            component={Link}
            to="/staff/members"
            variant="contained"
            color="primary"
            startIcon={<ListIcon />}
          >
            View Members
          </Button>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Members"
              value={stats.totalMembers}
              icon={<PeopleIcon />}
              color="#3f51b5"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Members"
              value={stats.activeMembers}
              icon={<CheckCircleIcon />}
              color="#4CAF50"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Members"
              value={stats.pendingMembers}
              icon={<PendingIcon />}
              color="#FF9800"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Expired Members"
              value={stats.expiredMembers}
              icon={<WarningIcon />}
              color="#F44336"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Members Section */}
      <Paper elevation={2} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} /> Recent Members
        </Typography>
        
        {recentMembers && recentMembers.length > 0 ? (
          <TableContainer>
            <Table size="medium">
              <TableHead sx={{ bgcolor: 'rgba(63, 81, 181, 0.08)' }}>
                <TableRow>
                  <TableCell>Member Name</TableCell>
                  <TableCell>Membership #</TableCell>
                  <TableCell>Join Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentMembers.slice(0, 5).map((member, index) => (
                  <TableRow key={member._id || index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            mr: 2, 
                            bgcolor: 'primary.light',
                            width: 36,
                            height: 36
                          }}
                        >
                          {member.userId?.firstName?.[0]}{member.userId?.lastName?.[0]}
                        </Avatar>
                        <Typography>
                          {member.userId?.firstName} {member.userId?.lastName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{member.membershipNumber || 'N/A'}</TableCell>
                    <TableCell>{new Date(member.joinDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={member.status?.toUpperCase() || 'UNKNOWN'} 
                        size="small"
                        color={
                          member.status === 'active' ? 'success' : 
                          member.status === 'pending' ? 'warning' : 
                          'error'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        component={Link}
                        to={`/staff/members/${member._id}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<ArrowForwardIcon />}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No recent members found</Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            component={Link} 
            to="/staff/members"
            color="primary"
            endIcon={<ArrowForwardIcon />}
          >
            View All Members
          </Button>
        </Box>
      </Paper>

      {/* Quick Access Cards Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Tasks and Assignments Card - Staff specific */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
              },
              position: 'relative'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: 12, 
              right: 12, 
              backgroundColor: 'rgba(93, 64, 153, 0.1)',
              borderRadius: '50%',
              p: 1
            }}>
              <AssignmentIcon fontSize="large" sx={{ color: '#5D4099' }} />
            </Box>
            <CardContent sx={{ flexGrow: 1, pt: 4 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                My Tasks
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                View and manage your assigned tasks and responsibilities.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, pt: 0 }}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => navigate('/staff/tasks')}
                startIcon={<AssignmentIcon />}
                sx={{ borderRadius: 1, bgcolor: '#5D4099', '&:hover': { bgcolor: '#4A3380' } }}
              >
                View My Tasks
              </Button>
            </Box>
          </Card>
        </Grid>
        
        {/* Workflow Dashboard Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 20px rgba(0,0,0,0.15)'
              },
              position: 'relative'
            }}
          >
            <Box sx={{ 
              position: 'absolute', 
              top: 12, 
              right: 12, 
              backgroundColor: 'rgba(63, 81, 181, 0.1)',
              borderRadius: '50%',
              p: 1
            }}>
              <BarChartIcon fontSize="large" color="primary" />
            </Box>
            <CardContent sx={{ flexGrow: 1, pt: 4 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Workflow Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Access analytics and insights for all workflow processes.
              </Typography>
            </CardContent>
            <Box sx={{ p: 2, pt: 0 }}>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => navigate('/staff/workflow-dashboard')}
                startIcon={<DashboardIcon />}
                sx={{ borderRadius: 1 }}
              >
                View Dashboard
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default StaffDashboardPage;
